import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import useAuth from 'hooks/useAuth';
import useCommunities from 'hooks/useCommunities';
import { useSearchParams } from 'react-router-dom';

type WorkspaceValue = {
  activeCommunityId: string; // '' means All Communities
  includeAll: boolean;
  loading: boolean;
  error: unknown;
  communities: any[];
  hasManagementRole: boolean;
  selected: { id: string; name: string } | null;
  selectCommunity: (id: string | 'all') => Promise<void>;
  boundsMap: Record<string, any>; // bounds keyed by documentId
  activeCommunityBounds: any | null; // bounds for current selection
};

const Ctx = createContext<WorkspaceValue | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user, setOnboardingComplete } = useAuth() as any;
  const { communities, loading, error, hasManagementRole, updateActiveCommunity } = useCommunities();
  const [activeCommunityId, setActiveCommunityId] = useState<string>(''); // '' = All by default
  const [activeCommunityBounds, setActiveCommunityBounds] = useState<any | null>(null);
  const [, setSearchParams] = useSearchParams();

  const includeAll = communities.length > 1;

  // Memoized bounds map keyed by documentId
  const boundsMap = useMemo(() => {
    const map: Record<string, any> = {};
    communities.forEach((c: any) => {
      const key = String(c?.documentId ?? c?.id ?? '');
      if (key && c?.bounds) map[key] = c.bounds;
    });
    return map;
  }, [communities]);

  // derive user active id (supports id/documentId/string)
  const userActiveId = useMemo(() => {
    const a: any = (user as any)?.app_active_community;
    if (!a) return '';
    if (typeof a === 'string' || typeof a === 'number') return String(a);
    return String(a?.documentId ?? a?.id ?? '');
  }, [user]);

  // Single effect: priority = URL param > user profile > '' (All Communities).
  // Only selects a specific community if it exists in the loaded list.
  useEffect(() => {
    if (loading || communities.length === 0) return;

    const resolve = (id: string) =>
      communities.some((c: any) => String(c?.documentId ?? c?.id ?? '') === id) ? id : null;

    const urlId = new URLSearchParams(window.location.search).get('app_active_community') || '';
    const singleId = communities.length === 1 ? String((communities[0] as any)?.documentId ?? (communities[0] as any)?.id ?? '') : '';
    const nextId = resolve(urlId) ?? resolve(userActiveId) ?? singleId ?? '';

    const nextBounds = nextId ? boundsMap[nextId] || null : null;

    if (activeCommunityId !== nextId) {
      setActiveCommunityId(nextId);
      setActiveCommunityBounds(nextBounds);
    }

    // Keep URL in sync
    const currentUrlId = new URLSearchParams(window.location.search).get('app_active_community') || '';
    if (currentUrlId !== nextId) {
      const params = new URLSearchParams(window.location.search);
      if (nextId) {
        params.set('app_active_community', nextId);
      } else {
        params.delete('app_active_community');
      }
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, communities, userActiveId, boundsMap]);

  // Sync onboardingComplete whenever activeCommunityId or communities change
  useEffect(() => {
    if (loading) return;
    let value: boolean;
    if (activeCommunityId) {
      // Specific community selected — use its flag
      const activeCommunity = communities.find((c: any) => String(c?.documentId ?? c?.id ?? '') === activeCommunityId);
      value = (activeCommunity as any)?.onboardingComplete ?? false;
    } else {
      // All Communities — true if at least one community has completed onboarding
      value = communities.some((c: any) => (c as any)?.onboardingComplete === true);
    }
    setOnboardingComplete?.(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCommunityId, communities, loading]);

  // 3. In selectCommunity, only update state and bounds, not the URL
  const selectCommunity = useCallback(
    async (id: string | 'all') => {
      const toPersist = id === 'all' ? '' : String(id);
      
      // Only update if different
      if (toPersist === activeCommunityId) return;
      
      setActiveCommunityId(toPersist);
      setActiveCommunityBounds(toPersist ? boundsMap[toPersist] || null : null);
      
      // Update URL parameter when user selects a community
      const params = new URLSearchParams(window.location.search);
      if (toPersist) {
        params.set('app_active_community', toPersist);
      } else {
        params.delete('app_active_community');
      }
      setSearchParams(params, { replace: true });
      
      if (user?.id) {
        await updateActiveCommunity(user.id, toPersist);
      }
    },
    [updateActiveCommunity, user?.id, boundsMap, setSearchParams, activeCommunityId]
  );

  const selected = useMemo(() => {
    if (activeCommunityId === '') {
      if (communities.length === 1) {
        const only = communities[0] as any;
        return { id: String(only?.documentId ?? only?.id ?? ''), name: only?.name ?? 'Select' };
      }
      return { id: '', name: 'All Communities' };
    }
    const c = communities.find((x: any) => String(x?.documentId ?? x?.id ?? '') === activeCommunityId);
    return c ? { id: activeCommunityId, name: c.name ?? `Community ${c.documentId}` } : null;
  }, [activeCommunityId, communities]);

  const value: WorkspaceValue = {
    activeCommunityId,
    includeAll,
    loading,
    error,
    communities,
    hasManagementRole,
    selected,
    selectCommunity,
    boundsMap,
    activeCommunityBounds
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
